@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.CustomerApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private fun cusRechnungSnapshotValue(container: All?, key: String): String =
    when (key) {
        "istAbgeschlossen" -> if (container?.abgeschlossenAm.orEmpty().isNotBlank()) "true" else ""
        "abgeschlossenAm" -> container?.abgeschlossenAm
        "onboardingPreis" -> container?.onboardingPreis
        "lizensierungPreis" -> container?.lizensierungPreis
        "crStd" -> container?.crStd
        "crKosten" -> container?.crKosten
        "supportTickets" -> container?.supportTickets
        "supportKosten" -> container?.supportKosten
        "meetingStd" -> container?.meetingStd
        "meetingKosten" -> container?.meetingKosten
        "schulungStd" -> container?.schulungStd
        "schulungKosten" -> container?.schulungKosten
        "beratungStd" -> container?.beratungStd
        "beratungKosten" -> container?.beratungKosten
        "gesamtbetragEuro" -> container?.gesamtbetragEuro
        else -> ""
    }.orEmpty()

private fun cusRechnungEmptySnapshot(kunde: All, monthKey: String) = All(
    id = "rechnung_${monthKey}_${kunde.firmenId}",
    firmenId = kunde.firmenId,
    firma = kunde.firma,
    lizenzkey = kunde.lizenzkey,
    datum = monthKey,
    verbrauch = emptyList()
)

@Composable
fun CUSRechnungsuebersicht() {
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    var kunde by remember { mutableStateOf<All?>(null) }
    var aktuellerMonat by remember { mutableStateOf<All?>(null) }
    var monatsrechnungen by remember { mutableStateOf<List<All>>(emptyList()) }

    val monthKey = remember {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
    }

    suspend fun currentFirmenId(): String {
        val nodeStatus = try {
            window.fetch("/node/status").then { it.json() }.await()
        } catch (_: Throwable) {
            null
        }
        val nodeFirmenId = nodeStatus?.asDynamic()?.firmenId as? String ?: ""
        return LoginService.getUser()?.firmenId.orEmpty()
            .ifBlank { GlobalManager.currentFirmenId }
            .ifBlank { nodeFirmenId }
    }

    suspend fun loadCurrentKunde(): All? {
        val resolvedFirmenId = currentFirmenId()
        if (resolvedFirmenId.isBlank()) return null
        val base = RegistrierungApi.loadCompanies()
            .filter { it.istkunde }
            .firstOrNull { it.firmenId == resolvedFirmenId || it.id == resolvedFirmenId }
            ?: All(id = resolvedFirmenId, firmenId = resolvedFirmenId)
        val setup = CustomerApi.loadDetails(resolvedFirmenId)
        return base.copy(
            id = resolvedFirmenId,
            firmenId = resolvedFirmenId,
            firma = setup?.firma.orEmpty().ifBlank { base.firma },
            lizenzkey = setup?.lizenzkey.orEmpty(),
            nodePort = setup?.nodePort.orEmpty(),
            zustand = setup?.zustand ?: base.zustand
        )
    }

    fun mapRechnungRow(currentKunde: All, rechnung: All?): List<String> = listOf(
        rechnung?.id.orEmpty().ifBlank { "${currentKunde.firmenId}_$monthKey" },
        currentKunde.firmenId,
        currentKunde.firma,
        currentKunde.lizenzkey,
        rechnung?.datum.orEmpty().ifBlank { monthKey },
        cusRechnungSnapshotValue(rechnung, "onboardingPreis"),
        cusRechnungSnapshotValue(rechnung, "lizensierungPreis"),
        cusRechnungSnapshotValue(rechnung, "crStd"),
        cusRechnungSnapshotValue(rechnung, "crKosten"),
        cusRechnungSnapshotValue(rechnung, "supportTickets"),
        cusRechnungSnapshotValue(rechnung, "supportKosten"),
        cusRechnungSnapshotValue(rechnung, "meetingStd"),
        cusRechnungSnapshotValue(rechnung, "meetingKosten"),
        cusRechnungSnapshotValue(rechnung, "schulungStd"),
        cusRechnungSnapshotValue(rechnung, "schulungKosten"),
        cusRechnungSnapshotValue(rechnung, "beratungStd"),
        cusRechnungSnapshotValue(rechnung, "beratungKosten"),
        cusRechnungSnapshotValue(rechnung, "gesamtbetragEuro")
    )

    fun mapMonatsRow(rechnung: All): List<String> = listOf(
        rechnung.id.ifBlank { "${rechnung.firmenId}_${rechnung.datum}" },
        rechnung.datum,
        cusRechnungSnapshotValue(rechnung, "istAbgeschlossen"),
        cusRechnungSnapshotValue(rechnung, "abgeschlossenAm"),
        cusRechnungSnapshotValue(rechnung, "onboardingPreis"),
        cusRechnungSnapshotValue(rechnung, "lizensierungPreis"),
        cusRechnungSnapshotValue(rechnung, "crStd"),
        cusRechnungSnapshotValue(rechnung, "crKosten"),
        cusRechnungSnapshotValue(rechnung, "supportTickets"),
        cusRechnungSnapshotValue(rechnung, "supportKosten"),
        cusRechnungSnapshotValue(rechnung, "meetingStd"),
        cusRechnungSnapshotValue(rechnung, "meetingKosten"),
        cusRechnungSnapshotValue(rechnung, "schulungStd"),
        cusRechnungSnapshotValue(rechnung, "schulungKosten"),
        cusRechnungSnapshotValue(rechnung, "beratungStd"),
        cusRechnungSnapshotValue(rechnung, "beratungKosten"),
        cusRechnungSnapshotValue(rechnung, "gesamtbetragEuro")
    )

    LaunchedEffect(Unit) {
        val loadedKunde = loadCurrentKunde()
        kunde = loadedKunde
        aktuellerMonat = null
        monatsrechnungen = emptyList()
    }

    val currentKunde = kunde
    if (currentKunde == null) {
        Div { Text("Kein Kunde gefunden.") }
        return
    }

    TabularSplitScreen(
        initialRatio = 0.56f,
        screen1 = {
            TabularScreen(
                screenKey = "CUSRechnungsuebersicht",
                rollenKey = "CUSRechnungsuebersicht",
                title = "Rechnungsübersicht aktuell ($monthKey)",
                headers = listOf(
                    "ID", "Firmen-ID", "Firma", "Lizenzkey", "Monat",
                    "Onboarding Preis", "Lizensierung Preis",
                    "CR STD", "CR Kosten",
                    "SUP-Ticket", "Support Kosten",
                    "Meeting STD", "Meeting Kosten",
                    "Schulung STD", "Schulung Kosten",
                    "Beratung STD", "Beratung Kosten",
                    "Gesamt EUR"
                ),
                data = aktuellerMonat?.let { listOf(currentKunde.id to mapRechnungRow(currentKunde, it)) }.orEmpty(),
                editable = false,
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = false,
                onSearchWithFilters = { filters ->
                    scope.launch {
                        aktuellerMonat = CustomerApi.searchVerbrauch(monthKey, filters).firstOrNull()
                    }
                },
                onCommit = {}
            )
        },
        screen2 = {
            TabularScreen(
                screenKey = "CUSRechnungsuebersicht_${currentKunde.id}",
                rollenKey = "CUSRechnungsuebersicht",
                title = "Monate ${currentKunde.firma.ifBlank { currentKunde.firmenId }}",
                headers = listOf(
                    "ID", "Monat", "Abgeschlossen", "Abgeschlossen am",
                    "Onboarding Preis", "Lizensierung Preis",
                    "CR STD", "CR Kosten",
                    "SUP-Ticket", "Support Kosten",
                    "Meeting STD", "Meeting Kosten",
                    "Schulung STD", "Schulung Kosten",
                    "Beratung STD", "Beratung Kosten",
                    "Gesamt EUR"
                ),
                data = monatsrechnungen.map { rechnung ->
                    rechnung.id.ifBlank { "${currentKunde.firmenId}_${rechnung.datum}" } to mapMonatsRow(rechnung)
                },
                editable = false,
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                allowSave = false,
                nonEditableCols = setOf("ID"),
                onSearchWithFilters = { filters ->
                    scope.launch {
                        monatsrechnungen = CustomerApi.searchVerbrauchMonate(filters)
                            .sortedByDescending { it.datum }
                    }
                },
                onCommit = {}
            )
        }
    )
}
